import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang, useL } from "@/lib/i18n";
import { api, errDetail, fmtDate, fmtDateTime } from "@/lib/api";
import { StatusBadge } from "@/components/member/Lists";
import { IncidentPhotos } from "@/components/member/IncidentPhotos";
import { Card, GhostBtn } from "./ui";

const ONB = ["not_started", "appointment_pending", "appointment_confirmed", "identity_verified", "tour_completed", "keycard_issued", "access_activated"];

function OnbRow({ m, onDone }) {
  const { t, tErr } = useLang();
  const L = useL();
  const [st, setSt] = useState(m.onboarding.status);
  const [appt, setAppt] = useState(m.onboarding.appointment || "");
  const save = async () => {
    try { await api.put(`/admin/members/${m.id}/onboarding`, { status: st, appointment: appt || null, notes: m.onboarding.notes || "" }); toast.success("OK"); onDone(); }
    catch (e) { toast.error(tErr(errDetail(e))); }
  };
  return (
    <li className="flex flex-wrap items-center gap-2 border-b border-white/5 py-3" data-testid={`onb-row-${m.id}`}>
      <div className="min-w-[180px] flex-1"><p className="text-white">{m.name}</p><p className="text-xs text-slate-500">{m.email} · <StatusBadge s={m.membership_status || "none"} /></p>
        {m.onboarding.verified_at && <p className="text-xs text-emerald-300">{L("Identité vérifiée", "ID verified")} {fmtDateTime(m.onboarding.verified_at)} · {m.onboarding.verified_by}</p>}</div>
      <input value={appt} onChange={(e) => setAppt(e.target.value)} placeholder="YYYY-MM-DD HH:MM" className="h-9 w-40 rounded-lg border border-white/15 bg-black/40 px-2 font-mono text-xs text-white" data-testid={`onb-appt-${m.id}`} />
      <select value={st} onChange={(e) => setSt(e.target.value)} className="h-9 rounded-lg border border-white/15 bg-black/40 px-2 text-sm text-white" data-testid={`onb-status-${m.id}`}>
        {ONB.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
      </select>
      <GhostBtn onClick={save} data-testid={`onb-save-${m.id}`}>{L("Enregistrer", "Save")}</GhostBtn>
    </li>
  );
}

export function AdminOnboarding() {
  const L = useL();
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/admin/onboarding").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card title={L("Accueil des membres", "Member onboarding")} testId="admin-onboarding">
      <p className="mb-3 text-xs text-slate-500">{L("Vérification d'identité en personne seulement : aucune copie ni numéro de pièce n'est enregistré. Seuls le statut, la date et l'administrateur sont conservés. Gérez les plages offertes dans Réglages.", "In-person ID check only: no copies or document numbers are stored. Only status, date and administrator are kept. Manage offered slots in Settings.")}</p>
      <ul>{list.map((m) => <OnbRow key={m.id} m={m} onDone={load} />)}</ul>
      {!list.length && <p className="text-sm text-slate-500">—</p>}
    </Card>
  );
}

export function AdminActivity() {
  const { lang } = useLang();
  const L = useL();
  const [s, setS] = useState([]);
  const [inc, setInc] = useState([]);
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/sessions").then((r) => setS(r.data));
    api.get("/admin/incidents").then((r) => setInc(r.data));
    api.get("/admin/notification-log").then((r) => setNotes(r.data));
    api.get("/admin/audit").then((r) => setAudit(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const review = async (i) => {
    const decision = window.prompt(L("Décision / suivi :", "Decision / follow-up:"), i.decision || "");
    if (decision === null) return;
    await api.put(`/admin/incidents/${i.id}`, { status: "reviewed", decision, notified_member: window.confirm(L("Membre avisé ?", "Member notified?")) });
    load();
  };
  return (
    <div className="space-y-6">
      <Card title={L("Sessions actives et checkout", "Active sessions & checkout")} testId="admin-sessions">
        <ul className="space-y-1 text-sm">{s.map((b) => (
          <li key={b.id} className="flex flex-wrap justify-between gap-2 border-b border-white/5 py-2 text-slate-300" data-testid={`admin-session-${b.id}`}>
            <span className="capitalize">{fmtDate(b.local_date, lang)} · {b.local_start}–{b.local_end} · {b.user_name}</span>
            <span className="font-mono text-xs">{b.checked_out_at ? `${L("Checkout", "Checked out")} ${fmtDateTime(b.checked_out_at, lang)}` : b.checkout_unconfirmed ? <span className="text-amber-300">{L("Checkout non confirmé", "Checkout unconfirmed")}</span> : "—"}
              {" · "}{L("Rappels", "Reminders")} 15:{b.reminders.rem_15 ? "✓" : "–"} 5:{b.reminders.rem_5 ? "✓" : "–"} {L("fin", "end")}:{b.reminders.rem_end ? "✓" : "–"}</span>
          </li>))}</ul>
        {!s.length && <p className="text-sm text-slate-500">—</p>}
        <p className="mt-2 text-xs text-slate-500">{L("Un checkout manquant ne prouve pas que le membre est encore sur place. Le site ne contrôle aucune porte.", "A missing checkout does not prove the member is still inside. The website controls no doors.")}</p>
      </Card>
      <Card title={L("Incidents (privés)", "Incidents (private)")} testId="admin-incidents">
        <ul className="space-y-2 text-sm">{inc.map((i) => (
          <li key={i.id} className="rounded-lg border border-white/10 p-3 text-slate-300"><p className="text-xs text-slate-500">{fmtDateTime(i.created_at, lang)} · {i.user_name} · {i.status}</p><p className="mt-1">{i.description}</p><IncidentPhotos inc={i} />
            {i.decision && <p className="mt-1 text-xs text-emerald-300">{i.decision}</p>}<GhostBtn className="mt-2" onClick={() => review(i)} data-testid={`incident-review-${i.id}`}>{L("Consigner une décision", "Record decision")}</GhostBtn></li>))}</ul>
        {!inc.length && <p className="text-sm text-slate-500">—</p>}
      </Card>
      <Card title={L("Envois (courriel / in-app)", "Deliveries (email / in-app)")} testId="admin-deliveries">
        <ul className="max-h-72 space-y-1 overflow-y-auto font-mono text-xs text-slate-400">{notes.map((n) => <li key={n.id}>{fmtDateTime(n.created_at, lang)} · {n.kind} · <span className={n.email_status === "failed" ? "text-red-400" : n.email_status === "sent" ? "text-emerald-300" : ""}>{n.email_status}</span></li>)}</ul>
      </Card>
      <Card title={L("Historique admin (audit)", "Admin audit history")} testId="admin-audit">
        <ul className="max-h-72 space-y-1 overflow-y-auto font-mono text-xs text-slate-400">{audit.map((a) => <li key={a.id}>{fmtDateTime(a.at, lang)} · {a.admin} · {a.method} {a.path}</li>)}</ul>
      </Card>
    </div>
  );
}
